import { SplideProps } from '@splidejs/react-splide'
import { Splide } from '@splidejs/splide'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { up } from '@Globals/Style/breakpoints'

const StyledIndicatorWrapper = styled.aside(({ theme }) => css`
  background: ${theme.colors.greyLighterer};
  height: 2px;
  margin: 20px 10px 0;
  overflow: hidden;
  position: relative;

  ${up('desktop')} {
    margin: 20px 0 0;
  }

  &[hidden] {
    display: none;
  }

,
`)

const StyledIndicator = styled.div<{ dragging?: boolean }>(({ dragging, theme }) => css`
  background: ${theme.colors.greyLight};
  height: 100%;
  position: absolute;
  transition: ${dragging ? undefined : 'left 100ms ease-out'};
`)

type UseSplideProgressBarProps = {
  allowOvershoot?: boolean
  perPage?: number
}

export const useSplideProgressBar = ({
  allowOvershoot,
  perPage = 3,
}: UseSplideProgressBarProps = {}) => {
  const positionWrapperRef = useRef<HTMLDivElement>(null)
  const positionIndicatorRef = useRef<HTMLDivElement>(null)
  const [isEnabled, setIsEnabled] = useState(false)
  const [dragging, setDragging] = useState(false)

  const disableScrollThingie = useCallback(() => {
    if (positionWrapperRef.current) {
      positionWrapperRef.current.hidden = true
    }
    setIsEnabled(false)
  }, [])

  const enableScrollThingie = useCallback(() => {
    if (positionWrapperRef.current) {
      positionWrapperRef.current.hidden = false
    }
    setIsEnabled(true)
  }, [])

  // A handy function to actually make sure the progress bar looks right.
  const update = useCallback((splide: Splide) => {
    const { track } = splide.Components.Elements
    const { getLimit, getPosition } = splide.Components.Move

    if (splide.Components.Slides.getLength(true) <= perPage) {
      disableScrollThingie()
      return
    }

    if (positionIndicatorRef.current && positionWrapperRef.current) {
      const availableTravelDistance = Math.abs(getLimit(true))

      if (availableTravelDistance) {
        const trackFrameWidth = track.getBoundingClientRect().width
        const totalTrackLength = trackFrameWidth + availableTravelDistance

        const currentPosition = allowOvershoot
          ? -getPosition()
          // The min/max stuff here is to provide overflow-control. The slider can be dragged beyond its limits,
          // and we don't want the progress bar to follow the same pattern.
          : Math.min(Math.max(-getPosition(), 0), availableTravelDistance)

        // This calculation might seem a bit off, but we don't want to know how far they've actually come, but
        // rather how far to move the indicator compared to its width in order to position it correctly.
        const travelledPercentage = Math.round((currentPosition / totalTrackLength) * 1000) / 10

        positionIndicatorRef.current.style.width = `${Math.round((trackFrameWidth / totalTrackLength) * 1000) / 10}%`

        // "left" is used, because translations would be either pixel values (which is dumb), or a percentage of
        // the element's width, which is terrible. This is much easier.
        positionIndicatorRef.current.style.left = `${travelledPercentage}%`
        if (!isEnabled) {
          enableScrollThingie()
        }
      } else if (isEnabled) {
        // Seems we haven't got enough slider to move. So let's not.
        disableScrollThingie()
      }
    }
  }, [allowOvershoot, disableScrollThingie, enableScrollThingie, isEnabled, perPage])

  const onSliderMounted: SplideProps['onMounted'] = useCallback((splide) => {
    window.setTimeout(() => update(splide), 100)
  }, [update])

  const progressBar = useMemo(() => (
    <StyledIndicatorWrapper aria-hidden hidden ref={positionWrapperRef}>
      <StyledIndicator dragging={dragging} ref={positionIndicatorRef}>
        &nbsp;
      </StyledIndicator>
    </StyledIndicatorWrapper>
  ), [dragging])

  return {
    isEnabled,
    onSliderMounted,
    progressBar,
    setDragging,
    dragging,
    update,
  }
}
